import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
} from '@angular/core';
import { Job } from '../../class/job.class';
import { DotStatusComponent } from '../status-dot/status-dot.component';
import { LucideAngularModule } from 'lucide-angular';
import {
  AsyncPipe,
  NgClass,
  NgStyle,
  TitleCasePipe,
  UpperCasePipe,
} from '@angular/common';
import { TimerPipe } from '../../pipes/timer.pipe';
import { ResponsiveService } from '../../services/reponsive.service';
import { Observable } from 'rxjs';
import {
  animate,
  keyframes,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AutoCapitalizePipe } from '../../pipes/auto-capitalize.pipe';
import { ModalService } from '../../services/modal.service';
import { DownloadService } from '../../services/download.service';
import { JobService } from '../../services/job.service';

@Component({
  selector: 'app-job-row',
  imports: [
    DotStatusComponent,
    LucideAngularModule,
    NgStyle,
    AsyncPipe,
    NgClass,
    UpperCasePipe,
    TimerPipe,
    TranslateModule,
    AutoCapitalizePipe,
  ],
  templateUrl: './job-row.component.html',
  styleUrl: './job-row.component.scss',
  animations: [
    trigger('animation', [
      transition(':enter', [
        animate(
          '0.3s',
          keyframes([
            style({ opacity: 0.2, translate: '-20% 0', offset: 0 }),
            style({ opacity: 1, translate: '0 0', offset: 1 }),
          ]),
        ),
      ]),
      transition(':leave', [
        animate(
          '0.3s',
          keyframes([
            style({ opacity: 0.2, translate: '20% 0', offset: 0 }),
            style({ opacity: 1, translate: '0 0', offset: 1 }),
          ]),
        ),
      ]),
    ]),
    trigger('menuAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95) translateY(-10px)' }),
        animate(
          '150ms ease-out',
          style({ opacity: 1, transform: 'scale(1) translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '100ms ease-in',
          style({ opacity: 0, transform: 'scale(0.95) translateY(-10px)' }),
        ),
      ]),
    ]),
  ],
  host: {
    '[@animation]': '',
  },
})
export class JobRowComponent {
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private router: Router = inject(Router);
  private elementRef: ElementRef = inject(ElementRef);
  private modalService: ModalService = inject(ModalService);
  private downloadService: DownloadService = inject(DownloadService);
  private jobService: JobService = inject(JobService);

  @Input() job!: Job;
  @Output() onClick: EventEmitter<Job> = new EventEmitter<Job>();
  isDesktop$: Observable<boolean> = this.responsiveService.desktop$;
  openMenu: boolean = false;
  private static currentOpenMenu: JobRowComponent | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedOnThisMenuButton = this.elementRef.nativeElement
      .querySelector('#menu-button')
      ?.contains(event.target as Node);
    if (!clickedOnThisMenuButton) {
      this.openMenu = false;
    }
  }

  toggleMenu(event: Event) {
    event.stopPropagation();
    if (
      JobRowComponent.currentOpenMenu &&
      JobRowComponent.currentOpenMenu !== this
    ) {
      JobRowComponent.currentOpenMenu.openMenu = false;
    }
    this.openMenu = !this.openMenu;
    JobRowComponent.currentOpenMenu = this.openMenu ? this : null;
  }

  downloadAudio() {
    this.jobService.getAudioFinal(this.job.id).subscribe({
      next: (blob: Blob) => {
        const filename = `${this.job.title
          .replace(/[^a-z0-9]/gi, '_')
          .toLowerCase()}_${this.job.id}.wav`;
        this.downloadService.downloadFile(blob, filename);
      },
      error: (error) => {
        console.error("Erreur lors du téléchargement de l'audio:", error);
      },
    });
    this.openMenu = false;
  }

  downloadVideo() {
    this.downloadService.getVideoFinalStream(this.job.id).subscribe({
      next: (blob: Blob) => {
        const filename = `${this.job.title
          .replace(/[^a-z0-9]/gi, '_')
          .toLowerCase()}_${this.job.id}.mp4`;
        this.downloadService.downloadFile(blob, filename);
        this.openMenu = false;
      },
      error: (error) => {
        console.error('Erreur lors du téléchargement de la vidéo:', error);
      },
    });
  }

  viewDetails() {
    this.router.navigate(['/dashboard/job', this.job.id]);
    this.openMenu = false;
  }

  editVideo() {
    this.router.navigate(['/dashboard/job', this.job.id]);
    this.openMenu = false;
  }

  editTranscription() {
    this.router.navigate([
      '/dashboard/job/' + this.job.id + '/transcription-editing',
    ]);
    this.openMenu = false;
  }

  deleteVideo() {
    this.modalService.openDeleteJobModal(this.job.id);
    this.openMenu = false;
  }
}
